/**
 * The internal dev calling: a movement-first test kit for crossing maps fast on a local or test stack.
 *
 * Game data stays in TOML — the hotbar and its level-one curve live in
 * `content/classes/{hotbars,ability_curve}.toml` `[dev]`. This module owns the typed tuning the engine
 * applies to a dev character (stats, movement, cooldown scale, mana), shared by server and client so
 * the cast gate and its prediction agree. Dev characters are never created on prod
 * (`isDevCallingAllowed`).
 */
import { CharacterClass, type CharacterStats, type MovementStats } from '../CharacterTypes';

export interface DevCallingConfig {
  readonly name: string;
  readonly description: string;
  readonly color: string;
  readonly baseStats: CharacterStats;
  readonly healthMultiplier: number;
  readonly manaMultiplier: number;
  /** Below the server's horizontal speed cap, so Sprint and Divine Steed are not rejected as teleports. */
  readonly movementStats: MovementStats;
  /** Multiplier on every authored ability cooldown. Charge abilities (Lanternskip) keep their own recharge. */
  readonly cooldownScale: number;
  /** Casts spend no mana. */
  readonly infiniteMana: boolean;
}

export const DEV_CALLING: DevCallingConfig = {
  name: 'Developer',
  description: 'Internal test calling: every movement ability, short cooldowns and free casts.',
  color: '#FF00FF',
  baseStats: {
    strength: 99, dexterity: 99, intelligence: 99, luck: 99,
    attack: 999, magicAttack: 999, defense: 99, magicDefense: 99, criticalRate: 0.5,
  } as CharacterStats,
  healthMultiplier: 10,
  manaMultiplier: 10,
  movementStats: {
    jumpPower: 1300,
    targetJumpHeight: 220,
    moveSpeed: 400,
    gravityScale: 0.8,
    terminalVelocity: 1200,
    acceleration: 5000,
    deceleration: 8000,
  },
  cooldownScale: 0.1,
  infiniteMana: true,
};

export function isDevCalling(heroClass: unknown): boolean {
  return heroClass === CharacterClass.DEV;
}

/** The cooldown a cast actually starts: scaled for the dev calling, unchanged for everyone else. */
export function callingCooldownMs(heroClass: unknown, cooldownMs: number): number {
  return isDevCalling(heroClass) ? Math.round(cooldownMs * DEV_CALLING.cooldownScale) : cooldownMs;
}

/** Same policy as the dev token: anywhere but prod. `environment` is the server's `H2000_ENV` class. */
export function isDevCallingAllowed(environment: 'dev' | 'staging' | 'prod'): boolean {
  return environment !== 'prod';
}
